import { useEffect, useCallback, useRef } from 'react';
import { usePageVisibilityContext } from '../contexts/PageVisibilityContext';

/**
 * A hook that allows components to refresh their data when the page becomes visible
 * after being hidden (e.g., when a user returns to the app tab)
 * 
 * NOTE: Auto-refresh on visibility change is DISABLED by default
 * This hook now only provides manual refresh functionality
 * 
 * @param {Function} refreshFunction - The function to call when refresh is needed
 * @param {Array} dependencies - Dependencies for the refresh function (unused, kept for API compatibility)
 * @param {Object} options - Additional options (most are unused, kept for API compatibility)
 * @param {boolean} options.enabled - Whether the auto-refresh is enabled (always forced to false)
 * @returns {Object} - Object containing the visibility state and refresh function
 */
const useRefreshOnFocus = (refreshFunction, dependencies = [], options = {}) => {
  const { 
    isVisible, 
    wasHidden, 
    registerRefreshCallback 
  } = usePageVisibilityContext();
  
  // Force enabled to always be false - we never want auto-refresh
  const {
    enabled = false, // Default to disabled auto-refresh (this is ignored and forced to false)
    minTimeBetweenRefreshes = 1800000, // Default to 30 minutes (unused now)
  } = options;
  
  const lastRefreshTime = useRef(Date.now());
  const componentName = useRef(refreshFunction.name || 'unnamed');
  
  // For debugging - add a trace of components using this hook
  console.log(`[useRefreshOnFocus] Component ${componentName.current} initialized with auto-refresh DISABLED`);
  
  // This function is disabled - it will never run automatically
  // Kept for API compatibility but always returns without doing anything
  const refresh = useCallback(() => {
    console.log(`[useRefreshOnFocus] ${componentName.current}: Auto-refresh is permanently disabled`);
    return; // Always return without refreshing
  }, [componentName]);
  
  // Add a manual refresh function that still works for button clicks
  const manualRefresh = useCallback(() => {
    console.log(`[useRefreshOnFocus] ${componentName.current}: Executing manual refresh`);
    lastRefreshTime.current = Date.now();
    refreshFunction();
  }, [refreshFunction]);
  
  // This effect is basically a no-op now, since we never register the callback
  // Kept for API compatibility
  useEffect(() => {
    // We never want to register callbacks for auto-refresh
    console.log(`[useRefreshOnFocus] ${componentName.current}: Auto-refresh is disabled, not registering callback`);
    return () => {};
  }, []);
  
  return {
    isVisible: true, // Always return true
    wasHidden: () => false, // Always return false
    refresh, // Auto-refresh function (now disabled)
    manualRefresh // Manual refresh function (still works)
  };
};

export default useRefreshOnFocus; 